import { Button } from "components/atoms"
import styled from "styled-components"
import devices from "styles/devices"
import QuotationMark from "url:assets/images/illustrations/quotation-mark.png"

// Standard container for status pages
export const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  max-width: 978px;
  margin: 0 auto 32px;
`

// Standard header section
export const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  @media screen and ${devices.bp_lg} {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`

// Standard text content wrapper
export const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const Container = styled.div`
  max-width: 1500px;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  font-family: ${(props) => props.theme.fontFamily};
  position: relative;

  @media screen and ${devices.bp_lg} {
    flex-direction: row;
  }
`

export const ContentOuterContainer = styled.div<{ wider?: boolean }>`
  @media screen and ${devices.bp_lg} {
    margin: ${({ wider }) => (wider ? "8.5em 5em 0 6.25em" : "8.5em 12.5em 0 6.25em")};
  }
`

export const FullWidthContentContainer = styled.div`
  margin: 2em 2em 2em 1em;
  strong {
    font-weight: 550;
  }
  @media screen and ${devices.bp_lg} {
    width: 100%;
    margin: 0;
  }
`

export const Title = styled.h2<{ bottomGap?: boolean }>`
  font-size: 24px;
  margin-bottom: 24px;
  margin-top: 36px;
  word-break: break-word;
  line-height: 38px;
  font-weight: 550;

  @media screen and ${devices.bp_lg} {
    font-size: 28px;
    margin-bottom: 48px;
    margin-top: 0;
  }
`

export const TitleWithoutBottomGap = styled(Title)`
  margin-bottom: 0;
`

export const SmallTitle = styled.h4`
  font-size: 18px;
  font-weight: 500;
  line-height: 27px;
`

export const Text = styled.p`
  color: #333a53;
  line-height: 180%;
  margin: 16px 0;
`

export const TextSingleLine = styled(Text)`
  margin: 0;
`

export const SubHeading = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
`

export const BeforeYourCallInstructions = styled.div`
  line-height: 24px;
`

export const Flex = styled.div`
  display: flex;
  flex-direction: column;
`

export const SmallPrint = styled.div`
  margin-top: 2em;
  margin-bottom: 6em;
  p {
    margin: 0 0 1em 0;
    line-height: 18px;
    font-size: 12px;
    color: ${(props) => props.theme.shade20};
  }
  @media screen and ${devices.bp_lg} {
    margin-bottom: 0;
  }
`

export const LoaderContainer = styled.div`
  margin-bottom: 5em;
  text-align: center;
`

export const MirrorImage = styled.img`
  width: 100%;
  position: relative;
  top: 200px;
  left: -250px;
  right: 0;
  display: none;

  > img {
    width: 100%;
    height: auto;
  }

  @media screen and ${devices.bp_lg} {
    width: 600px;
    display: block;
  }
`

export const WonGraphic = styled.img`
  width: 100%;
  height: auto;
  position: relative;
  top: 100px;
  left: -150px;
  right: 0;
  display: none;

  > img {
    width: 100%;
    height: auto;
  }

  @media screen and ${devices.bp_lg} {
    width: auto;
    height: 587px;
    display: block;
  }
`

export const WonGraphicTop = styled.img`
  width: 100%;
  height: auto;
  position: relative;
  top: -30px;
  left: -150px;
  right: 0;
  display: none;

  > img {
    width: 100%;
    height: auto;
  }

  @media screen and ${devices.bp_lg} {
    width: auto;
    height: 650px;
    display: block;
  }
`

export const SideWrapper = styled.div`
  position: relative;
`

export const ChecklistContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5em;
  margin: 1em;
  bottom: 5em;

  @media screen and ${devices.bp_lg} {
    margin: 7em 2em 0 0;
    padding: 4em 4em 1.5em;
    width: 22.5em;
    bottom: 0;
  }
`

export const ChecklistHeader = styled.h2`
  font-size: 26px;
  margin: 0;
  font-weight: 550;
  line-height: 32px;
`

export const UploadDocsButtonContainer = styled.div`
  display: block;
  @media screen and ${devices.bp_lg} {
    display: inline-block;
  }
`

export const BoldText = styled(Text)`
  font-weight: bold;
`

export const BlueTextLink = styled.a`
  color: rgb(7, 62, 217);
  margin: 10px 0;
  display: block;
  text-decoration: underline;
`

export const SubTitle = styled.h6`
  font-size: 16px;
  margin-top: 36px;
  margin-bottom: 1em;
  line-height: 38px;
  font-weight: bold;
`

export const CardTitle = styled(SubTitle)`
  margin: 0;
  margin-top: 16px;
`
export const MiniLogo = styled.div`
  width: 50px;
  height: 50px;
  margin-right: 20px;
  background-image: url(${({
    backgroundImage,
  }: {
    backgroundImage: string
    borderRadius: boolean
  }) => backgroundImage});
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  border-radius: ${({ borderRadius }) => (borderRadius ? "50%" : "0%")};
`

export const ContentContainer = styled.div<{ wider?: boolean }>`
  margin: 2em 2em 2em 1em;
  strong {
    font-weight: 550;
  }
`

export const MainContent = styled.main`
  flex: 1;
  margin: 2em auto;
  position: relative;
  z-index: 1;
  max-width: 1100px;
  width: 100%;
  padding: 0 2em;

  @media screen and ${devices.bp_lg} {
    margin: 0 auto 4em auto;
    padding: 0 3em;
  }
`

export const HeroSection = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  padding: 24px 0 32px 0;
  align-items: center;

  @media screen and ${devices.bp_lg} {
    grid-template-columns: 1fr 1fr;
    gap: 64px;
    padding: 48px 0 12px 0;
  }
`

export const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  @media screen and ${devices.bp_lg} {
    width: 574px;
  }
`

export const HeroTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #000928;
  line-height: 1.3;
  margin: 0;

  @media screen and ${devices.bp_lg} {
    font-size: 36px;
  }
`

export const HeroText = styled.p`
  font-family: TT Wellingtons;
  font-weight: 500;
  font-style: normal;
  font-size: 16px;
  line-height: 180%;
  letter-spacing: 0%;
  font-variant-numeric: lining-nums proportional-nums;
  color: #000928;
  margin: 0;
`

export const HeroList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: #000928;
  font-weight: 500;
  font-style: Medium;
  font-size: 16px;
  line-height: 180%;
  letter-spacing: 0%;
  font-variant-numeric: lining-nums proportional-nums;

  li {
    margin-bottom: 8px;
  }

  b {
    font-weight: 600;
    font-style: Bold;
    font-size: 16px;
    line-height: 180%;
    letter-spacing: 0%;
    font-variant-numeric: lining-nums proportional-nums;
    color: #000928;
  }
`

export const HeroIllustration = styled.div`
  display: none;

  @media screen and ${devices.bp_lg} {
    display: flex;
    justify-content: start;
    align-items: flex-start;
    height: 100%;
    width: 100%;

    img {
      width: 320px;
      mix-blend-mode: darken;
      object-fit: contain;
      align-self: flex-end;
    }
  }
`

export const CTAButton = styled(Button)`
  width: 100%;
  background: #073ed9;
  color: #f8f9fc;
  padding: 16px 32px;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  border-bottom: 8px #001bbe solid;
  transition: all 0.2s;

  &:hover {
    background: #0056b3;
  }

  @media screen and ${devices.bp_lg} {
    width: 100%;
  }
`

export const FreeConsultationText = styled.p`
  font-family: TT Wellingtons;
  font-weight: 500;
  font-style: Medium;
  font-size: 16px;
  line-height: 180%;
  letter-spacing: 0%;
  font-variant-numeric: lining-nums proportional-nums;
  color: ${(props) => props.theme.shade?.["20"] || "#3B4258"};
  margin: 0;
`

export const SectionCard = styled.section`
  background: white;
  width: 100%;
  max-width: 1100px;
  border-radius: 24px;
  padding: 24px;
  margin: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media screen and ${devices.bp_lg} {
    height: 348px;
    padding: 40px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    gap: 32px;
  }
`

export const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media screen and ${devices.bp_lg} {
    width: 534px;
    height: 268px;
    justify-content: center;
    gap: 10px;
  }
`

export const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => props.theme.shade?.["0"] || "#3F4148"};
  margin: 0 0 12px 0;
  line-height: 1.3;

  @media screen and ${devices.bp_lg} {
    font-size: 24px;
    margin: 0 0 16px 0;
  }
`

export const SectionText = styled.p`
  font-size: 14px;
  color: ${(props) => props.theme.shade?.["20"] || "#3B4258"};
  line-height: 1.7;
  font-weight: 500;
  margin: 0 0 20px 0;

  @media screen and ${devices.bp_lg} {
    font-size: 15px;
  }
`

export const SectionIllustration = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  order: -1;

  img {
    width: 100%;
    height: 200px;
    border-radius: 8px;
    object-fit: cover;
  }

  @media screen and ${devices.bp_lg} {
    order: 0;

    img {
      width: -webkit-fill-available;
      height: 250px;
    }
  }
`

export const LearnMoreButton = styled.button`
  width: 100%;
  background: transparent;
  border: 2px solid black;
  border-radius: 8px;
  padding: 15px 20px;
  color: black;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: black;
    color: white;
  }

  @media screen and ${devices.bp_lg} {
    font-size: 16px;
    padding: 12px 24px;
  }
`

export const Disclaimer = styled.p({
  fontStyle: "italic",
  marginTop: "2em",
  marginBottom: "2em",
})

export const TrustpilotSection = styled.section`
  border-radius: 24px;
  padding: 0;
  margin: 64px 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;

  @media screen and ${devices.bp_lg} {
    padding: 32px 32px 0 32px;
    flex-direction: row;
    gap: 64px;
    justify-content: center;
    align-items: flex-start;
  }
`

export const TrustpilotTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => props.theme.shade?.["0"] || "#3F4148"};
  margin: 0 0 24px 0;

  @media screen and ${devices.bp_lg} {
    font-size: 24px;
  }
`

export const ReviewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;

  @media screen and ${devices.bp_lg} {
    width: 574px;
    height: 336px;
    flex-shrink: 0;

    &::before {
      content: "";
      position: absolute;
      top: -70px;
      left: -100px;
      width: calc(100% + 80px);
      height: calc(100% + 40px);
      background-image: url(${QuotationMark});
      background-repeat: no-repeat;
      background-position: 0 0;
      background-size: auto;
      z-index: -1;
      pointer-events: none;
    }
  }
`

export const ReviewQuote = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const ReviewTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => props.theme.shade?.["0"] || "#3F4148"};
  margin: 0;
  line-height: 1.3;
`

export const ReviewText = styled.p`
  font-size: 16px;
  color: ${(props) => props.theme.shade?.["10"] || "#61676e"};
  line-height: 1.6;
  margin: 0;
`

export const ReviewMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`

export const ReviewAuthor = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
`

export const StarRating = styled.div`
  color: #00b67a;
  font-size: 18px;
  line-height: 1;
`

export const AuthorName = styled.span`
  color: #6e717c;
  font-size: 16px;
  font-weight: 500;
`

export const ReviewDate = styled.span`
  color: #6e717c;
  font-size: 16px;
`

export const TrustpilotContainer = styled.div`
  width: 100%;
  max-width: 370px;
  height: 300px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and ${devices.bp_lg} {
    width: 370px;
    flex-shrink: 0;
  }
`

export const Footer = styled.footer`
  color: #6e717c;
  margin-top: 32px;
`

export const FooterLogo = styled.img`
  width: 110px;
  height: 32px;
  margin-bottom: 16px;
`

export const FooterText = styled.p`
  font-family: TT Wellingtons;
  font-weight: 500;
  font-size: 14px;
  line-height: 180%;
  margin: 0;
`
